import { Activity, Check, Clock, Download, FileJson, User } from "lucide-react";
import React, { type FC, useEffect, useState } from "react";

import { formatDateOnly, RebootLiveModal, StatCard } from "./RebootLiveModal";

export interface ExportResult {
  filename: string;
  sizeBytes: number;
}

export interface ExportChatModalProps {
  isOpen: boolean;
  onClose: () => void;
  playerName: string;
  movementType: string;
  selectedDate: string;
  messageCount: number;
  onExport: () => undefined | Promise<ExportResult | undefined>;
}

type ExportPhase = "configure" | "preparing" | "complete";

export const ExportChatModal: FC<ExportChatModalProps> = ({
  isOpen,
  onClose,
  playerName,
  movementType,
  selectedDate,
  messageCount,
  onExport,
}) => {
  const [phase, setPhase] = useState<ExportPhase>("configure");
  const [preparingProgress, setPreparingProgress] = useState(0);
  const [exportResult, setExportResult] = useState<ExportResult | null>(null);

  useEffect(() => {
    if (isOpen) {
      setPhase("configure");
      setPreparingProgress(0);
      setExportResult(null);
    }
  }, [isOpen]);

  const handleExport = async () => {
    setPhase("preparing");
    setPreparingProgress(0);

    const steps = [
      { progress: 25, delay: 200 },
      { progress: 55, delay: 500 },
      { progress: 80, delay: 800 },
      { progress: 100, delay: 1100 },
    ];

    steps.forEach(({ progress, delay }) => {
      setTimeout(() => setPreparingProgress(progress), delay);
    });

    const [result] = await Promise.all([
      Promise.resolve(onExport()),
      new Promise((resolve) => setTimeout(resolve, 1400)),
    ]);

    if (result) {
      setExportResult(result);
      setPhase("complete");
    } else {
      onClose();
    }
  };

  const sessionDateDisplay =
    selectedDate && selectedDate !== "N/A"
      ? formatDateOnly(selectedDate)
      : new Date().toLocaleDateString("en-US", {
          month: "short",
          day: "numeric",
          year: "numeric",
        });

  const isPreparing = phase === "preparing";
  const isComplete = phase === "complete";

  const getTitle = () => {
    if (isPreparing) return "Preparing export...";
    if (isComplete) return "Export Complete";
    return "Export Chat Session";
  };

  const getSubtitle = () => {
    if (isPreparing) return `Packaging ${messageCount} messages`;
    if (isComplete) return "Your chat session has been downloaded";
    return "Beta feature · Exports expire after 30 days · Import to continue conversation";
  };

  const getIcon = () => {
    if (isComplete) {
      return <Check className="size-5 text-orange-500" />;
    }
    return <FileJson className="size-5 text-orange-500" />;
  };

  const formatFileSize = (bytes: number) => {
    if (bytes < 1024) return `${bytes} B`;
    return `${(bytes / 1024).toFixed(1)} KB`;
  };

  const sessionPreviewCards = (
    <div className="px-6 pb-4">
      <div className="grid grid-cols-3 gap-2">
        <StatCard
          icon={<User className="size-3.5" />}
          value={playerName || "Player"}
          label="Player"
          truncate
          dataTestId="export-preview-player"
        />
        <StatCard
          icon={<Activity className="size-3.5" />}
          value={movementType || "Swing"}
          label="Movement"
          truncate
          dataTestId="export-preview-movement"
        />
        <StatCard
          icon={<Clock className="size-3.5" />}
          value={sessionDateDisplay}
          label="Session Date"
          truncate
          dataTestId="export-preview-session-date"
        />
      </div>
    </div>
  );

  return (
    <RebootLiveModal
      isOpen={isOpen}
      onClose={onClose}
      title={getTitle()}
      dataTestId="export-chat-modal"
      subtitle={getSubtitle()}
      icon={getIcon()}
      canClose={!isPreparing}
      maxWidth="lg"
    >
      {phase === "configure" && (
        <>
          {sessionPreviewCards}
          <div className="flex items-center justify-between px-6 pt-3 pb-6">
            <button
              type="button"
              onClick={onClose}
              data-testid="export-chat-cancel-button"
              className="px-4 py-2 rounded-md text-[#7e7e81] hover:text-white hover:bg-[#252527] transition-colors text-sm"
            >
              Cancel
            </button>
            <button
              type="button"
              onClick={handleExport}
              data-testid="export-chat-download-button"
              className="flex items-center gap-2 px-5 py-2.5 rounded-md text-sm font-semibold transition-all bg-orange-500 text-white hover:bg-orange-600 shadow-[0px_0px_20px_-4px_rgba(251,106,7,0.4)]"
            >
              <Download className="size-3.5" />
              Download .json
            </button>
          </div>
        </>
      )}

      {phase === "preparing" && (
        <div className="px-6 py-12 flex flex-col items-center gap-6">
          <div
            className="size-12 rounded-full border-2 border-[#252527] border-t-orange-500 animate-spin"
            aria-hidden
          />
          <div className="w-full max-w-[280px] h-1 bg-[#252527] rounded-full overflow-hidden">
            <div
              className="h-full bg-orange-500 rounded-full transition-all duration-300"
              style={{ width: `${preparingProgress}%` }}
            />
          </div>
        </div>
      )}

      {phase === "complete" && exportResult && (
        <>
          {sessionPreviewCards}
          <div className="px-6 pb-4">
            <div
              className="flex items-center gap-3 px-4 py-3 rounded-lg bg-[#111113] border border-[rgba(255,255,255,0.04)]"
              data-testid="export-complete-file-info"
            >
              <div className="shrink-0 size-9 rounded-lg bg-orange-500/15 flex items-center justify-center">
                <FileJson className="size-4 text-orange-500" />
              </div>
              <div className="min-w-0 flex-1">
                <p className="text-white text-sm font-medium truncate">
                  {exportResult.filename}
                </p>
                <p className="text-[#7e7e81] text-xs mt-0.5">
                  {formatFileSize(exportResult.sizeBytes)} · {messageCount}{" "}
                  {messageCount === 1 ? "message" : "messages"}
                </p>
              </div>
            </div>
          </div>
          <div className="px-6 pb-6 flex justify-end">
            <button
              type="button"
              onClick={onClose}
              data-testid="export-chat-done-button"
              className="px-5 py-2.5 rounded-md text-sm font-semibold transition-all bg-[#252527] text-white hover:bg-[#2a2a2c]"
            >
              Done
            </button>
          </div>
        </>
      )}
    </RebootLiveModal>
  );
};
